import Modal, { Props as ModalProps } from '@hero/ui-kit/components/Modal';
import Button from '@hero/ui-kit/inputs/Button';
import Form, { useFormContext } from '@hero/ui-kit/inputs/Form';
import Input from '@hero/ui-kit/inputs/Input';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import React from 'react';
import { object, string } from 'yup';
import useMemberReEnroll from '../../api/useMemberReEnroll';
import { MemberReEnrollResponse } from '../../types';

const reEnrollSchema = object({
    adminEmail: string().email('Please enter a valid email address.').required('Email is required.')
});

type ReEnrollFormValues = { adminEmail: string };

type ReEnrollModalProps = {
    memberName: string;
    memberId: number;
    deviceSerial?: string | null;
    prefillAdminEmail?: string | null;
    onSuccess?: (response: MemberReEnrollResponse) => void;
    onError?: () => void;
} & Pick<ModalProps, 'externalControls'>;

type ReEnrollFormProps = {
    memberName: string;
    deviceSerial?: string | null;
    isPending: boolean;
};

const ReEnrollForm: React.FC<ReEnrollFormProps> = ({ memberName, deviceSerial, isPending }) => {
    const { formState } = useFormContext();

    return (
        <Section centered>
            <H role="h5">Re-enroll member</H>
            {deviceSerial && <P>Device serial: {deviceSerial}</P>}
            <P>You are about to re-enroll {memberName}. A new member record will be created.</P>
            <Input name="adminEmail" displayName="Hero Admin Email" type="email" showErrMessage />
            <Button type="submit" disabled={isPending || !formState.isValid}>
                Re-enroll
            </Button>
        </Section>
    );
};

const ReEnrollModal: React.FC<ReEnrollModalProps> = ({
    externalControls,
    memberName,
    memberId,
    deviceSerial,
    prefillAdminEmail,
    onSuccess,
    onError
}) => {
    const { mutateAsync: reEnrollMember, isPending, isError, reset } = useMemberReEnroll();
    const [isOpen, setExternalState] = externalControls ?? [];

    React.useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const handleSubmit = ({ adminEmail }: ReEnrollFormValues) => {
        if (memberId <= 0) return;
        reEnrollMember({ payload: { member_id: memberId, admin_email: adminEmail } })
            .then((response) => {
                onSuccess && onSuccess(response);
                setExternalState && setExternalState(false);
            })
            .catch(() => {});
    };

    const handleGotIt = () => {
        setExternalState && setExternalState(false);
        onError && onError();
    };

    return (
        <Modal
            externalControls={externalControls}
            isCancelable
            styles={{ minWidth: '57rem', padding: 0 }}
        >
            {isError ? (
                <Section centered>
                    <H role="h5">Re-enrollment unavailable</H>
                    <P>
                        {memberName} cannot be re-enrolled at this time. The member&apos;s status
                        may have changed. Please refresh the page and try again.
                    </P>
                    <Button onClick={handleGotIt}>Got it</Button>
                </Section>
            ) : (
                <Form
                    validationSchema={reEnrollSchema}
                    validationMode="onTouched"
                    submitFn={handleSubmit}
                    defaultValues={{ adminEmail: prefillAdminEmail ?? '' }}
                >
                    <ReEnrollForm
                        memberName={memberName}
                        deviceSerial={deviceSerial}
                        isPending={isPending}
                    />
                </Form>
            )}
        </Modal>
    );
};

export default ReEnrollModal;
